import React, { useContext, useEffect } from "react";
import { Divider, Grid, Typography } from "@mui/material";
import i18next from "i18next";
import { ThirdpartiesSearchAttributes } from "./ThirdpartiesSearchAttributes";
import { ThirdpartiesSearchIndicators } from "./ThirdpartiesSearchCalculations";
import { ThirdpartiesSearchIndued } from "./ThirdpartiesSearchIndued";
import EAAutoCompleteInput from "components/inputs/EAAutoCompleteInput";
import { useFormContext } from "react-hook-form";
import {
  PrivilegeEnum,
  RoleBasedAccessControlContext,
} from "services/common/RolesUtils";
import { useCallback } from "react";
import { Context } from "states/Store";
import { hasOption } from "services/common/Utils";
import useStyles from "../ThirdpartiesListSearchFormStyle";
import { Subscription, ThirdpartyScoresFieldsNames } from "services/common/Constants";
import { SearchFormInputBloc } from "components/lists-advanced-search/SearchFormInputBloc/SearchFormInputBloc";
import {
  ESG_SCORES_STRING_VALUES,
  FINANCIAL_STRENGTH_SCORES,
  getEsgDepths,
} from 'services/common/DataBlocksUtils';
import { ThirdpartiesSearchEcovadis } from "./ThirdpartiesSearchEcovadis";
import { ThirdpartiesSearchDataHub } from "./ThirdpartiesSearchDataHub";
import { ThirdpartiesSearchSecurityRating } from "./ThirdpartiesSearchSecurityRating";
import { ThirdpartiesSearchCreditsafe } from "./ThirdpartiesSearchCreditsafe";

export function ThirdpartiesAdvancedSearch({
  transparencyOptions,
  induedOptions,
  financialStrengthScoreOptions,
  financialStrengthRatingOptions,
  attributes,
  calculatedAttributes,
  indicators,
  filters,
}) {
  const { classes } = useStyles();
  const { setValue } = useFormContext();
  const [state,] = useContext(Context);
  const { hasAnyOfPrivileges } = useContext(RoleBasedAccessControlContext);

  useEffect(() => {
    // Set default values for theses inputs if the user's privilege allows it.
    setValue("transparency", (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE) && filters.transparency) || []);
    setValue("financialStrengthScore", (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE) && filters.financialStrengthScore) || []);
    setValue(ThirdpartyScoresFieldsNames.ESG_SCORE, (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE) && filters[ThirdpartyScoresFieldsNames.ESG_SCORE]) || []);
    setValue(ThirdpartyScoresFieldsNames.ESG_DEPTH, (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE) && filters[ThirdpartyScoresFieldsNames.ESG_DEPTH]) || []);
    setValue(ThirdpartyScoresFieldsNames.ESG_RISK_ENVIRONMENTAL, (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE) && filters[ThirdpartyScoresFieldsNames.ESG_RISK_ENVIRONMENTAL]) || []);
    setValue(ThirdpartyScoresFieldsNames.ESG_RISK_SOCIAL, (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE) && filters[ThirdpartyScoresFieldsNames.ESG_RISK_SOCIAL]) || []);
    setValue(ThirdpartyScoresFieldsNames.ESG_RISK_GOVERNANCE, (hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE) && filters[ThirdpartyScoresFieldsNames.ESG_RISK_GOVERNANCE]) || []);
  }, [filters, setValue, hasAnyOfPrivileges]);

  const hasTransparency = useCallback(() => {
    return (
      hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_TRANSPARENCY_SOURCE) &&
      hasOption(state.accounts, state.account.id, "TRANSPARENCY")
    );
  }, [hasAnyOfPrivileges, state.accounts, state.account]);

  const hasIndued = useCallback(() => {
    return (
      hasAnyOfPrivileges(PrivilegeEnum.PRIVILEGE_VIEW_INDUED_SOURCE) &&
      hasOption(state.accounts, state.account.id, "INDUED")
    );
  }, [hasAnyOfPrivileges, state.accounts, state.account]);

  const hasFinancialStrength = useCallback(() => {
    return (
      hasAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_VIEW_FINANCIAL_STRENGTH_SOURCE
      ) && hasOption(state.accounts, state.account.id, "FINANCIALSTRENGTH")
    );
  }, [hasAnyOfPrivileges, state.accounts, state.account]);

  const hasESG = useCallback(() => {
    return (
      hasAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_VIEW_DNB_ESG_SOURCE
      ) && hasOption(state.accounts, state.account.id, "ESG")
    );
  }, [hasAnyOfPrivileges, state.accounts, state.account]);

  const hasEcovadis = useCallback(() => {
    return (
      hasAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_VIEW_ECOVADIS_SOURCE
      ) && hasOption(state.accounts, state.account.id, Subscription.ECOVADIS)
    );
  }, [hasAnyOfPrivileges, state.accounts, state.account]);

  const hasCreditsafe = useCallback(() => {
    return (
      hasAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_VIEW_CREDITSAFE_SOURCE
      ) && hasOption(state.accounts, state.account.id, Subscription.CREDITSAFE)
    );
  }, [hasAnyOfPrivileges, state.accounts, state.account]);

  const hasDataHub = useCallback(() => {
    return (
      hasAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_VIEW_DATA_HUB_SOURCE
      ) && hasOption(state.accounts, state.account.id, "DATA_HUB")
    );
  }, [hasAnyOfPrivileges, state.accounts, state.account]);

  const hasSecurityRating = useCallback(() => {
    return (
      hasAnyOfPrivileges(
        PrivilegeEnum.PRIVILEGE_VIEW_SECURITY_RATING_SOURCE
      ) && hasOption(state.accounts, state.account.id, "SECURITY_RATING")
    );
  }, [hasAnyOfPrivileges, state.accounts, state.account]);

  return (
    <Grid item container spacing={2} padding={"32px"}>
      {hasIndued() && (
        <>
          <Typography className={classes.label}>{i18next.t("indued.label")}</Typography>
          <Grid item container spacing={2}>
            {/* indued */}
            <ThirdpartiesSearchIndued
              induedOptions={induedOptions}
              filters={filters}
            />
          </Grid>
        </>
      )}
      {hasTransparency() && (
        <>
          <Divider className={classes.advancedSearchDivider} />
          <Typography className={classes.label}>{i18next.t("transparency.label")}</Typography>
          <Grid item container spacing={2}>
            {/* Transparency */}
            <Grid item xs={4} sm={3} md={2}>
              <SearchFormInputBloc label={i18next.t("transparency.score")}>
                <EAAutoCompleteInput
                  fieldName="transparency"
                  options={transparencyOptions}
                  placeholder={i18next.t('thirdpartySearch.all')}
                  variant="outlined"
                  multiple
                  limitTags={2}
                />
              </SearchFormInputBloc>
            </Grid>
          </Grid>
        </>
      )}
      {hasEcovadis() && (
        <>
          <Divider className={classes.advancedSearchDivider} />
          <Typography className={classes.label}>{i18next.t("ecovadis.label")}</Typography>
          <Grid item container spacing={2}>
            {/* Ecovadis */}
            <ThirdpartiesSearchEcovadis
              filters={filters}
            />
          </Grid>
        </>
      )}
      {hasFinancialStrength() && (
        <>
          <Divider className={classes.advancedSearchDivider} />
          <Typography className={classes.label}>{i18next.t("datablocks.financialStrength.scoreLabel")}</Typography>
          <Grid item container spacing={2}>
            {/* Financial Strength risk segment*/}
            <Grid item xs={4} sm={3} md={2}>
              <SearchFormInputBloc
                label={i18next.t("datablocks.financialStrength.scoreLabel")}
              >
                <EAAutoCompleteInput
                  fieldName="financialStrengthScore"
                  options={financialStrengthScoreOptions?.filter(x => x.value !== FINANCIAL_STRENGTH_SCORES.VERY_HIGH)} // Remove very high from options
                  placeholder={i18next.t('thirdpartySearch.all')}
                  variant="outlined"
                  multiple
                  limitTags={2}
                />
              </SearchFormInputBloc>
            </Grid>
            {/* Financial Strength rating */}
            <Grid item xs={4} sm={3} md={2}>
              <SearchFormInputBloc
                label={i18next.t("datablocks.financialStrength.rating")}
              >
                <EAAutoCompleteInput
                  fieldName="financialStrengthRating"
                  options={financialStrengthRatingOptions}
                  placeholder={i18next.t('thirdpartySearch.all')}
                  variant="outlined"
                  multiple
                  limitTags={2}
                />
              </SearchFormInputBloc>
            </Grid>
          </Grid>
        </>
      )}
      {hasESG() && (
        <>
          <Divider className={classes.advancedSearchDivider} />
          <Typography className={classes.label}>{i18next.t("ESG.field.section_esg")}</Typography>
          <Grid item container spacing={2}>
            {/* ESG Global Score */}
            <Grid item xs={4} sm={3} md={2}>
              <SearchFormInputBloc
                label={i18next.t("ESG.labelScore")}
              >
                <EAAutoCompleteInput
                  fieldName={ThirdpartyScoresFieldsNames.ESG_SCORE}
                  options={ESG_SCORES_STRING_VALUES}
                  placeholder={i18next.t("all")}
                  variant="outlined"
                  multiple
                  limitTags={2}
                />
              </SearchFormInputBloc>
            </Grid>
            {/* ESG Depth score */}
            <Grid item xs={4} sm={3} md={2}>
              <SearchFormInputBloc
                label={i18next.t("ESG.scoreDepth")}
              >
                <EAAutoCompleteInput
                  fieldName={ThirdpartyScoresFieldsNames.ESG_DEPTH}
                  options={getEsgDepths()}
                  placeholder={i18next.t("all")}
                  variant="outlined"
                  multiple
                  limitTags={2}
                />
              </SearchFormInputBloc>
            </Grid>
            {/* ESG Environmental Score*/}
            <Grid item xs={4} sm={3} md={2}>
              <SearchFormInputBloc
                label={i18next.t("ESG.riskEnvironment")}
              >
                <EAAutoCompleteInput
                  fieldName={ThirdpartyScoresFieldsNames.ESG_RISK_ENVIRONMENTAL}
                  options={ESG_SCORES_STRING_VALUES}
                  placeholder={i18next.t("all")}
                  variant="outlined"
                  multiple
                  limitTags={2}
                />
              </SearchFormInputBloc>
            </Grid>
            {/* ESG Social Score*/}
            <Grid item xs={4} sm={3} md={2}>
              <SearchFormInputBloc
                label={i18next.t("ESG.riskSocial")}
              >
                <EAAutoCompleteInput
                  fieldName={ThirdpartyScoresFieldsNames.ESG_RISK_SOCIAL}
                  options={ESG_SCORES_STRING_VALUES}
                  placeholder={i18next.t("all")}
                  variant="outlined"
                  multiple
                  limitTags={2}
                />
              </SearchFormInputBloc>
            </Grid>
            {/* ESG Governance Score*/}
            <Grid item xs={4} sm={3} md={2}>
              <SearchFormInputBloc
                label={i18next.t("ESG.riskGovernance")}
              >
                <EAAutoCompleteInput
                  fieldName={ThirdpartyScoresFieldsNames.ESG_RISK_GOVERNANCE}
                  options={ESG_SCORES_STRING_VALUES}
                  placeholder={i18next.t("all")}
                  variant="outlined"
                  multiple
                  limitTags={2}
                />
              </SearchFormInputBloc>
            </Grid>
          </Grid>
        </>
      )}
      {hasSecurityRating() && (
        <>
          <Divider className={classes.advancedSearchDivider} />
          <Typography className={classes.label}>{i18next.t("securityRating.label")}</Typography>
          <ThirdpartiesSearchSecurityRating filters={filters} />
        </>
      )}
      {hasCreditsafe() && (
        <>
          <Divider className={classes.advancedSearchDivider} />
          <Typography className={classes.label}>{i18next.t("creditsafe.label")}</Typography>
          <Grid item container spacing={2}>
            {/* Creditsafe */}
            <ThirdpartiesSearchCreditsafe filters={filters} />
          </Grid>
        </>
      )}
      {/* Attributes and calculated Attributes*/}
      <Divider className={classes.advancedSearchDivider} />
      <Typography className={classes.label}>{i18next.t("thirdpartySheet.items.attributes")}</Typography>
      <Grid item container spacing={2}>
        <ThirdpartiesSearchAttributes
          attributes={attributes}
          calculatedAttributes={calculatedAttributes}
        />
      </Grid>
      {/* Indicators */}
      <Divider className={classes.advancedSearchDivider} />
      <Typography className={classes.label}>{i18next.t("thirdpartySheet.items.indicators")}</Typography>
      <Grid item container spacing={2}>
        <ThirdpartiesSearchIndicators indicators={indicators} />
      </Grid>
      {hasDataHub() && (
        <>
          <Divider className={classes.advancedSearchDivider} />
          <Typography className={classes.label}>{i18next.t("thirdpartySheet.items.datahub")}</Typography>
          <Grid item container spacing={2}>
            {/* Data Hub */}
            <ThirdpartiesSearchDataHub />
          </Grid>
        </>
      )}

      {/* Unmanaged */}
      <Divider className={classes.advancedSearchDivider} />
      <Grid item xs={4} sm={3} md={2}>
        <SearchFormInputBloc
          label={i18next.t("unmanagedThirdParty.unmanagedFilterLabel")}
          hasTooltip
        >
          <EAAutoCompleteInput
            fieldName={"unmanaged"}
            options={[
              { value: "null", label: i18next.t("all") },
              { value: "true", label: i18next.t("yes") },
              { value: "false", label: i18next.t("no") },
            ]}
            variant="outlined"
            placeholder={i18next.t("all")}
            style={{
              "& .MuiFormControl-root": {
                "& .MuiAutocomplete-inputRoot": {
                  paddingLeft: "7px"
                },
              },
            }}
          />
        </SearchFormInputBloc>
      </Grid>
    </Grid>
  );
}
